import {
  AnalysisVisualizationType,
  ChartConfig,
  ChartOptionType,
} from 'analysis-tool/ui/charting/ChartingTypes';

/**
 * Map from AnalysisVisualizationType to the ChartOptionType used by getDefaultChartConfig.
 * BAR_CHART defaults to GROUPED_BAR. TABLE is not included since it's not a chart.
 */
export const ANALYSIS_VISUALIZATION_TYPE_TO_CHART_OPTION_TYPE: Record<
  Exclude<
    AnalysisVisualizationType,
    AnalysisVisualizationType.TABLE | AnalysisVisualizationType.CONTOUR_PLOT
  >,
  ChartOptionType
> = {
  [AnalysisVisualizationType.BAR_CHART]: ChartOptionType.GROUPED_BAR,
  [AnalysisVisualizationType.BOX_PLOT]: ChartOptionType.BOXPLOT,
  [AnalysisVisualizationType.HEATMAP]: ChartOptionType.HEATMAP,
  [AnalysisVisualizationType.LINE_CHART]: ChartOptionType.LINE,
  [AnalysisVisualizationType.PIE_CHART]: ChartOptionType.PIE,
  [AnalysisVisualizationType.SCATTER_PLOT]: ChartOptionType.SCATTER,
};

/**
 * Fields that are commonly shared across chart types that use x/y axis patterns.
 * When switching between chart types that both have these fields, preserve the user's values.
 */
const SHARED_XY_FIELDS = ['xAxisColumnName', 'yAxisColumnName', 'seriesColumnName'] as const;

/**
 * Fields that are shared across most chart types for legend configuration.
 */
const SHARED_LEGEND_FIELDS = ['showLegend', 'legendLocation'] as const;

const FIELDS_TO_PRESERVE = new Set<string>([...SHARED_XY_FIELDS, ...SHARED_LEGEND_FIELDS]);

/**
 * Preserves shared fields when switching between chart types.
 * Given an old chart config and a new default chart config, copies over field values
 * that exist in both configs' parameters.
 *
 * Uses Object.entries to iterate parameters as plain [key, value] pairs, which avoids
 * needing type assertions on the discriminated union parameter types.
 */
export function preserveSharedFields(oldConfig: ChartConfig, newConfig: ChartConfig): ChartConfig {
  const newParamKeys = new Set(Object.keys(newConfig.parameters));

  // Collect preserved values from the old config for fields present in both configs
  const preservedEntries = Object.entries(oldConfig.parameters).filter(
    ([key]) => FIELDS_TO_PRESERVE.has(key) && newParamKeys.has(key)
  );

  return {
    ...newConfig,
    parameters: {
      ...newConfig.parameters,
      ...Object.fromEntries(preservedEntries),
    },
  } as ChartConfig;
}
